<template>
  <div class="device-management">
    <div class="page-header">
      <h2>设备管理</h2>
      <div class="header-actions">
        <el-button 
          v-if="isAdmin"
          type="success" 
          @click="initializeFields"
          :loading="initializing"
        >
          <el-icon><Refresh /></el-icon>
          初始化字段
        </el-button>
        <el-button 
          v-if="isAdmin"
          type="primary" 
          @click="showAddDialog = true"
        >
          <el-icon><Plus /></el-icon>
          添加设备
        </el-button>
      </div>
    </div>
    
    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input
        v-model="searchText"
        placeholder="搜索设备名称或编号"
        style="width: 300px; margin-right: 10px;"
        clearable
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-button @click="() => { currentPage = 0; fetchDevices(); }">搜索</el-button>
      
      <div style="display: flex; gap: 10px; margin-left: 20px;" v-if="isAdmin">
        <el-button type="success" @click="downloadTemplate">
          <el-icon><Download /></el-icon>
          下载模板
        </el-button>
        <el-upload
          ref="uploadRef"
          :action="uploadUrl"
          :headers="uploadHeaders"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
          :before-upload="beforeUpload"
          :show-file-list="false"
          accept=".xlsx,.xls"
        >
          <el-button type="warning">
            <el-icon><Upload /></el-icon>
            Excel导入
          </el-button>
        </el-upload>
      </div>
    </div>
    
    <!-- 设备列表 -->
    <div class="device-list">
      <el-table 
        :data="filteredDevices" 
        style="width: 100%"
        v-loading="loading"
        row-key="id"
        :row-class-name="getRowClassName"
      >
        <!-- 序号列 -->
        <el-table-column label="序号" width="80" align="center">
          <template #default="{ $index }">
            <span style="font-weight: bold; color: #409EFF;">{{ currentPage * pageSize + $index + 1 }}</span>
          </template>
        </el-table-column>
        
        <!-- 动态字段 -->
        <el-table-column 
          v-for="field in visibleFields.filter(f => f.fieldName !== 'reviewDate')" 
          :key="field.fieldName"
          :prop="getFieldProp(field.fieldName)"
          :label="field.fieldLabel"
          :width="getColumnWidth(field.fieldType)"
          show-overflow-tooltip
        >
          <template #default="scope">
            <span v-if="field.fieldName === 'status'">
              <el-tag 
                :type="getStatusType(getFieldValue(scope.row, field.fieldName))"
              >
                {{ getStatusText(getFieldValue(scope.row, field.fieldName)) }}
              </el-tag>
            </span>
            <span v-else-if="field.fieldName === 'deviceType'">
              {{ getDeviceTypeText(getFieldValue(scope.row, field.fieldName)) }}
            </span>
            <span v-else-if="field.fieldType === 'BOOLEAN'">
              <el-tag :type="getFieldValue(scope.row, field.fieldName) ? 'success' : 'danger'">
                {{ getFieldValue(scope.row, field.fieldName) ? '是' : '否' }}
              </el-tag>
            </span>
            <span v-else-if="field.fieldType === 'DATE' || field.fieldType === 'DATETIME'">
              {{ formatDate(getFieldValue(scope.row, field.fieldName), field.fieldType) }}
            </span>
            <span v-else>
              {{ getFieldValue(scope.row, field.fieldName) || '-' }}
            </span>
          </template>
        </el-table-column>
      
        
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button size="small" @click="generateQRCode(scope.row)">二维码</el-button>
            <el-button 
              v-if="isAdmin" 
              size="small" 
              type="primary" 
              @click="editDevice(scope.row)"
              style="margin-left: 10px;"
            >
              编辑
            </el-button>
            <el-button 
              v-if="isAdmin" 
              size="small" 
              type="danger" 
              @click="deleteDevice(scope.row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页 -->
    <div style="display:flex; justify-content:flex-end; margin-top: 16px;">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="filteredTotal"
        :page-size="pageSize"
        :current-page="currentPage + 1"
        :page-sizes="[10, 15, 20, 30, 50]"
        @current-change="onPageChange"
        @size-change="onSizeChange"
      />
    </div>
    
    <!-- 添加/编辑设备对话框 -->
    <el-dialog 
      :title="editingDevice ? '' : '添加设备'"
      v-model="showAddDialog"
      width="600px"
      @close="resetForm"
    >
      <el-form 
        ref="deviceForm" 
        :model="deviceForm" 
        :rules="deviceRules"
        label-width="120px"
      >
        <!-- 动态字段 -->
        <div v-if="fieldConfigs.length > 0" class="dynamic-fields">
          <el-form-item 
            v-for="field in fieldConfigs" 
            :key="field.fieldName"
            :label="field.fieldLabel"
            :prop="`dynamicFields.${field.fieldName}`"
            :rules="getDynamicFieldRules(field)"
          >
            <!-- 文本输入 -->
            <el-input 
              v-if="field.fieldType === 'TEXT' || field.fieldType === 'EMAIL' || field.fieldType === 'PHONE' || field.fieldType === 'URL'"
              v-model="deviceForm.dynamicFields[field.fieldName]"
              :placeholder="field.placeholder || `请输入${field.fieldLabel}`"
              :type="getInputType(field.fieldType)"
            />
            
            <!-- 数字输入 -->
            <el-input-number 
              v-else-if="field.fieldType === 'NUMBER'"
              v-model="deviceForm.dynamicFields[field.fieldName]"
              :placeholder="field.placeholder || `请输入${field.fieldLabel}`"
              style="width: 100%"
            />
            
            <!-- 多行文本 -->
            <el-input 
              v-else-if="field.fieldType === 'TEXTAREA'"
              v-model="deviceForm.dynamicFields[field.fieldName]"
              type="textarea"
              :placeholder="field.placeholder || `请输入${field.fieldLabel}`"
              :rows="3"
            />
            
            <!-- 下拉选择 -->
            <el-select 
              v-else-if="field.fieldType === 'SELECT'"
              v-model="deviceForm.dynamicFields[field.fieldName]"
              :placeholder="field.placeholder || `请选择${field.fieldLabel}`"
              style="width: 100%"
            >
              <el-option 
                v-for="option in getFieldOptions(field.options)"
                :key="option.value || option"
                :label="option.label || option"
                :value="option.value || option"
              />
            </el-select>
            
            <!-- 单选 -->
            <el-radio-group 
              v-else-if="field.fieldType === 'RADIO'"
              v-model="deviceForm.dynamicFields[field.fieldName]"
            >
              <el-radio 
                v-for="option in getFieldOptions(field.options)"
                :key="option.value || option"
                :label="option.value || option"
              >
                {{ option.label || option }}
              </el-radio>
            </el-radio-group>
            
            <!-- 复选框 -->
            <el-checkbox 
              v-else-if="field.fieldType === 'CHECKBOX'"
              v-model="deviceForm.dynamicFields[field.fieldName]"
            >
              {{ field.fieldLabel }}
            </el-checkbox>
            
            <!-- 日期 -->
            <el-date-picker 
              v-else-if="field.fieldType === 'DATE'"
              v-model="deviceForm.dynamicFields[field.fieldName]"
              type="date"
              :placeholder="field.placeholder || `请选择${field.fieldLabel}`"
              style="width: 100%"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
            />
            
            <!-- 日期时间 -->
            <el-date-picker 
              v-else-if="field.fieldType === 'DATETIME'"
              v-model="deviceForm.dynamicFields[field.fieldName]"
              type="datetime"
              :placeholder="field.placeholder || `请选择${field.fieldLabel}`"
              style="width: 100%"
              format="YYYY-MM-DD HH:mm:ss"
              value-format="YYYY-MM-DD HH:mm:ss"
            />
            
            <!-- 布尔值 -->
            <el-switch 
              v-else-if="field.fieldType === 'BOOLEAN'"
              v-model="deviceForm.dynamicFields[field.fieldName]"
              active-text="是"
              inactive-text="否"
            />
          </el-form-item>
        </div>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showAddDialog = false">取消</el-button>
          <el-button type="primary" @click="saveDevice" :loading="saving">
            {{ editingDevice ? '更新' : '添加' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
    
    <!-- 二维码对话框 -->
    <el-dialog 
      title="设备二维码"
      v-model="showQRDialog"
      width="400px"
    >
      <div class="qr-container">
        <div ref="qrcode" class="qr-code"></div>
        <p class="qr-info">{{ selectedDevice?.deviceName }}</p>
        <p class="qr-number">设备编号: {{ selectedDevice?.deviceNumber }}</p>
      </div>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showQRDialog = false">关闭</el-button>
          <el-button type="primary" @click="downloadQRCode">下载二维码</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessageBox } from 'element-plus'
import { mapGetters, mapActions } from 'vuex'
import QRCode from 'qrcode'
import { Plus, Search, Refresh, Download, Upload } from '@element-plus/icons-vue'

export default {
  name: 'DynamicDeviceManagement',
  components: {
    Plus,
    Search,
    Refresh,
    Download,
    Upload
  },
  data() {
    return {
      devices: [],
      fieldConfigs: [],
      visibleFields: [],
      loading: false,
      saving: false,
      initializing: false,
      searchText: '',
      showAddDialog: false,
      showQRDialog: false,
      editingDevice: null,
      selectedDevice: null,
      deviceForm: {
        dynamicFields: {}
      },
      deviceRules: {},
      uploadUrl: '/api/admin/devices/excel-import',
      uploadHeaders: {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      },
      // 分页本地状态
      currentPage: 0,
      pageSize: 15,
      devicesTotal: 0
    }
  },
  computed: {
    ...mapGetters(['isAdmin']),
    filteredTotal() {
      const kw = (this.searchText || '').toLowerCase()
      return this.devices.filter(device => 
        device.deviceName?.toLowerCase().includes(kw) ||
        device.deviceNumber?.toLowerCase().includes(kw)
      ).length
    },
    filteredDevices() {
      const kw = (this.searchText || '').toLowerCase()
      const list = (!kw ? this.devices : this.devices.filter(device => 
        device.deviceName?.toLowerCase().includes(kw) ||
        device.deviceNumber?.toLowerCase().includes(kw)
      ))
        // 按设备编号字母升序（包含数字自然排序）
        .slice() // 避免原数组被排序
        .sort((a, b) => (a.deviceNumber || '').localeCompare(b.deviceNumber || '', 'zh-Hans-CN', { numeric: true, sensitivity: 'base' }))

      const start = this.currentPage * this.pageSize
      return list.slice(start, start + this.pageSize)
    }
  },
  mounted() {
    this.loadFieldConfigs()
    this.fetchDevices()

  },
  methods: {
    ...mapActions(['request']),
    
    async loadFieldConfigs() {
      try {
        const response = await this.request({
          url: '/api/admin/field-configs/visible',
          method: 'GET'
        })
        
        if (response.success) {
          this.fieldConfigs = response.data
          this.visibleFields = response.data.filter(field => field.visible)
        }
      } catch (error) {
        this.$message.error('获取字段配置失败')
      }
    },
    
    async fetchDevices() {
      this.loading = true
      try {
        // 统一获取完整列表，前端进行字母升序排序与本地分页
        const keyword = encodeURIComponent(this.searchText || '')
        const url = `/api/admin/devices?page=0&size=10000&keyword=${keyword}`
        const response = await this.request({
          url,
          method: 'GET'
        })
        
        // 兼容后端返回：{ success, data: [...], total, page, size, totalPages }
        if (response && response.success) {
          const data = Array.isArray(response.data) ? response.data : (response.data?.data || [])
          const total = response.total ?? (response.data?.total ?? data.length)
          // 前端本地分页，保持当前页与每页条数不变
          const page = this.currentPage
          const size = this.pageSize
          
          this.devices = data
          this.devicesTotal = Number(total) || 0
          this.currentPage = Number(page) || 0
          this.pageSize = Number(size) || 15
        } else {
          this.devices = []
          this.devicesTotal = 0
        }
      } catch (error) {
        this.devices = []
        this.devicesTotal = 0
        this.$message.error('获取设备列表失败')
      } finally {
        this.loading = false
      }
    },
    
    onPageChange(page) {
      // Element Plus 的页码从 1 开始
      this.currentPage = page - 1
      // 前端本地分页无需重新请求
    },
 
     onSizeChange(size) {
        this.pageSize = size
        this.currentPage = 0
        // 前端本地分页无需重新请求
       },
    
    async initializeFields() {
      this.initializing = true
      try {
        const response = await this.request({
          url: '/api/admin/field-configs/initialize',
          method: 'POST'
        })
        
        if (response.success) {
          this.$message.success('字段初始化成功')
          this.loadFieldConfigs()
        } else {
          this.$message.error(response.message || '字段初始化失败')
        }
      } catch (error) {
        this.$message.error('字段初始化失败')
      } finally {
        this.initializing = false
      }
    },
    
    editDevice(device) {
      this.editingDevice = device
      
      // 将设备基本属性添加到dynamicFields中，以便在表单中显示和编辑
      const dynamicFields = { ...device.dynamicFields }
      
      // 添加基础字段到dynamicFields
      dynamicFields.deviceNumber = device.deviceNumber || ''
      dynamicFields.deviceName = device.deviceName || ''
      dynamicFields.deviceType = device.deviceType || ''
      dynamicFields.location = device.location || ''
      dynamicFields.status = device.status || 'available'
      dynamicFields.description = device.description || ''
      
      this.deviceForm = {
        dynamicFields: dynamicFields
      }
      
      this.showAddDialog = true
    },
    
    async saveDevice() {
      try {
        await this.$refs.deviceForm.validate()
        
        this.saving = true
        
        // 从动态字段中提取基础设备信息
        const dynamicFields = { ...this.deviceForm.dynamicFields }
        // 确保状态值是正确的枚举格式（使用小写的value值）
        let statusValue = dynamicFields.status || 'available'
        if (typeof statusValue === 'string') {
          // 将前端的枚举名称转换为后端期望的value值
          const statusMap = {
            'AVAILABLE': 'available',
            'BORROWED': 'borrowed', 
            'MAINTENANCE': 'maintenance',
            'DAMAGED': 'damaged'
          }
          statusValue = statusMap[statusValue.toUpperCase()] || statusValue.toLowerCase()
        }
        
        const device = {
          deviceNumber: dynamicFields.deviceNumber || '',
          deviceName: dynamicFields.deviceName || '',
          deviceType: dynamicFields.deviceType || '',
          location: dynamicFields.location || '',
          status: statusValue,
          description: dynamicFields.description || ''
        }
        
        // 从动态字段中移除基础字段，避免重复
        const baseFields = ['deviceNumber', 'deviceName', 'deviceType', 'location', 'status', 'description']
        baseFields.forEach(field => {
          delete dynamicFields[field]
        })
        
        const requestData = {
          device: device,
          dynamicFields: dynamicFields
        }
        

        
        const url = this.editingDevice 
          ? `/api/admin/devices/${this.editingDevice.id}`
          : '/api/admin/devices'
        const method = this.editingDevice ? 'PUT' : 'POST'
        
        const response = await this.request({
          url,
          method,
          data: requestData
        })
        
        if (response.success) {
          this.$message.success(response.message || '保存成功')
          this.showAddDialog = false
          this.fetchDevices()
        } else {
          this.$message.error(response.message || '保存失败')
        }
      } catch (error) {
        this.$message.error('保存设备失败')
      } finally {
        this.saving = false
      }
    },
    
    async deleteDevice(device) {
      try {
        await ElMessageBox.confirm(
          `确定要删除设备 "${device.deviceName}" 吗？`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        const response = await this.request({
          url: `/api/admin/devices/${device.id}`,
          method: 'DELETE'
        })
        
        if (response.success) {
          this.$message.success('删除成功')
          this.fetchDevices()
        } else {
          this.$message.error(response.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除设备失败')
        }
      }
    },
    
    resetForm() {
      this.editingDevice = null
      this.deviceForm = {
        dynamicFields: {}
      }
      
      // 设置动态字段默认值
      this.fieldConfigs.forEach(field => {
        if (field.defaultValue) {
          this.deviceForm.dynamicFields[field.fieldName] = field.defaultValue
        }
      })
      
      if (this.$refs.deviceForm) {
        this.$refs.deviceForm.clearValidate()
      }
    },
    
    getDynamicFieldRules(field) {
      const rules = []
      
      if (field.required) {
        rules.push({
          required: true,
          message: `请输入${field.fieldLabel}`,
          trigger: field.fieldType === 'SELECT' ? 'change' : 'blur'
        })
      }
      
      if (field.validationRules) {
        try {
          const regex = new RegExp(field.validationRules)
          rules.push({
            pattern: regex,
            message: `${field.fieldLabel}格式不正确`,
            trigger: 'blur'
          })
        } catch (e) {
          // 忽略正则表达式解析错误，使用默认验证
        }
      }
      
      return rules
    },
    
    getInputType(fieldType) {
      const typeMap = {
        'EMAIL': 'email',
        'PHONE': 'tel',
        'URL': 'url',
        'TEXT': 'text'
      }
      return typeMap[fieldType] || 'text'
    },
    
    getFieldOptions(optionsString) {
      if (!optionsString) return []
      
      try {
        // 尝试解析JSON格式的选项
        const options = JSON.parse(optionsString)
        if (Array.isArray(options)) {
          return options
        }
      } catch (e) {
        // 如果不是JSON格式，按换行符分割
        return optionsString.split('\n').filter(option => option.trim())
      }
      
      return []
    },
    
    getColumnWidth(fieldType) {
      const widthMap = {
        'TEXT': 150,
        'NUMBER': 120,
        'EMAIL': 180,
        'PHONE': 140,
        'URL': 200,
        'TEXTAREA': 200,
        'SELECT': 120,
        'RADIO': 120,
        'CHECKBOX': 100,
        'DATE': 120,
        'DATETIME': 160,
        'BOOLEAN': 80
      }
      return widthMap[fieldType] || 150
    },
    
    formatDate(dateString, fieldType) {
      if (!dateString) return '-'
      
      try {
        const date = new Date(dateString)
        if (fieldType === 'DATE') {
          return date.toLocaleDateString('zh-CN')
        } else if (fieldType === 'DATETIME') {
          return date.toLocaleString('zh-CN')
        }
        return dateString
      } catch (e) {
        return dateString
      }
    },
    
    getStatusType(status) {
      const statusMap = {
        'AVAILABLE': 'success',
        'BORROWED': 'warning',
        'MAINTENANCE': 'danger',
        'DAMAGED': 'danger',
        'RETIRED': 'info',
        'available': 'success',
        'borrowed': 'warning',
        'maintenance': 'danger',
        'damaged': 'danger',
        'retired': 'info'
      }
      return statusMap[status] || 'info'
    },
    
    getDeviceTypeText(deviceType) {
      const deviceTypeMap = {
        'computer': '电脑',
        'printer': '打印机',
        'projector': '投影仪',
        'gps': 'GPS',
        'level': '水准仪',
        'walkie_talkie': '对讲机',
        'depth_sounder': '测深仪',
        'total_station': '全站仪',
        'laser_scanner': '激光扫描仪',
        'rangefinder': '测距仪',
        'unmanned_survey_vessel': '无人测量船',
        'drone': '无人机',
        'other': '其他'
      }
      return deviceTypeMap[deviceType] || deviceType
    },

    getStatusText(status) {
      const statusMap = {
        'AVAILABLE': '可用',
        'BORROWED': '已借出',
        'MAINTENANCE': '维护中',
        'DAMAGED': '损坏',
        'RETIRED': '已退役',
        'available': '可用',
        'borrowed': '已借出',
        'maintenance': '维护中',
        'damaged': '损坏',
        'retired': '已退役'
      }
      return statusMap[status] || status
    },
    
    async generateQRCode(device) {
      this.selectedDevice = device
      this.showQRDialog = true
      
      await this.$nextTick()
      
      try {
        // 清理之前的二维码
        const container = this.$refs.qrcode
        if (container) {
          container.innerHTML = ''
          
          // 创建新的 canvas 元素
          const canvas = document.createElement('canvas')
          container.appendChild(canvas)
          
          // 生成包含设备信息的二维码数据
          const qrData = JSON.stringify({
            id: device.id,
            deviceNumber: device.deviceNumber,
            deviceName: device.deviceName,
            deviceType: device.deviceType,
            location: device.location,
            status: device.status,
            dynamicFields: device.dynamicFields
          })
          
          await QRCode.toCanvas(canvas, qrData, {
            width: 200,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          })
        }
      } catch (error) {
        this.$message.error('生成二维码失败')
      }
    },
    
    downloadQRCode() {
      try {
        const canvas = this.$refs.qrcode.querySelector('canvas')
        if (canvas) {
          const link = document.createElement('a')
          link.download = `${this.selectedDevice.deviceNumber}_qrcode.png`
          link.href = canvas.toDataURL()
          link.click()
        }
      } catch (error) {
        this.$message.error('下载二维码失败')
      }
    },
    
    // 获取字段属性路径
    getFieldProp(fieldName) {
      // 基础字段直接从设备对象获取，动态字段从dynamicFields获取
      const baseFields = ['deviceNumber', 'deviceName', 'deviceType', 'location', 'status', 'description']
      if (baseFields.includes(fieldName)) {
        return fieldName
      }
      return `dynamicFields.${fieldName}`
    },
    
    // 获取字段值
    getFieldValue(device, fieldName) {
      // 基础字段直接从设备对象获取，动态字段从dynamicFields获取
      const baseFields = ['deviceNumber', 'deviceName', 'deviceType', 'location', 'status', 'description']
      if (baseFields.includes(fieldName)) {
        return device[fieldName] || ''
      }
      return device.dynamicFields ? (device.dynamicFields[fieldName] || '') : ''
    },
    
    // Excel导入相关方法
    async downloadTemplate() {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/admin/devices/excel-template', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.ok) {
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = '设备导入模板.xlsx'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
          this.$message.success('模板下载成功')
        } else {
          this.$message.error('模板下载失败')
        }
      } catch (error) {
        this.$message.error('模板下载失败')
      }
    },
    
    beforeUpload(file) {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                     file.type === 'application/vnd.ms-excel'
      const isLt10M = file.size / 1024 / 1024 < 10
      
      if (!isExcel) {
        this.$message.error('只能上传 Excel 文件！')
        return false
      }
      if (!isLt10M) {
        this.$message.error('上传文件大小不能超过 10MB！')
        return false
      }
      
      // 更新上传头部的token
      this.uploadHeaders.Authorization = 'Bearer ' + localStorage.getItem('token')
      
      return true
    },
    
    handleUploadSuccess(response) {
      if (response.success) {
        this.$message.success(response.message || '导入成功')
        this.fetchDevices() // 刷新设备列表
      } else {
        this.$message.error(response.message || '导入失败')
      }
    },
    
    handleUploadError() {
      this.$message.error('文件上传失败')
    },
    
    // 判断当前日期距离送审日期是否在30天以内
    isWithin30Days(reviewDate) {
      if (!reviewDate) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0) // 设置为当天开始时间
      const review = new Date(reviewDate)
      review.setHours(0, 0, 0, 0) // 设置为当天开始时间
      
      const diffTime = today - review
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      // 判断当前日期距离送审日期是否在30天以内
      return diffDays >= 0 && diffDays <= 30
    },
    
    // 获取表格行的CSS类名
    getRowClassName({ row }) {
      if (this.isWithin30Days(row.reviewDate)) {
        return 'review-warning-row'
      }
      return ''
    }
  }
}
</script>

<style scoped>
.device-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.search-bar {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.device-list {
  background: white;
  border-radius: 4px;
  overflow: hidden;
}

.dynamic-fields {
  margin-top: 20px;
}

.qr-container {
  text-align: center;
  padding: 20px;
}

.qr-code {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
}

.qr-info {
  font-size: 16px;
  font-weight: bold;
  margin: 10px 0 5px 0;
  color: #303133;
}

.qr-number {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.dialog-footer {
  text-align: right;
}

:deep(.el-table th) {
  background-color: #f5f7fa;
}

:deep(.el-dialog__body) {
  max-height: 60vh;
  overflow-y: auto;
}

/* 送审日期在30天内的行背景色 */
:deep(.review-warning-row) {
  background-color: #fef0f0 !important;
}

:deep(.review-warning-row:hover) {
  background-color: #fde2e2 !important;
}
</style>